<template>
  <PageLayout
    :title="$t('studioApi.playground_title')"
    :subtitle="$t('studioApi.playground_subtitle')"
  >
    <div class="playground">
      <div class="playground__request userDatatable global-shadow border bg-white radius-xl">
        <div class="playground__section">
          <label class="playground__label">{{ $t('studioApi.api_key') }}</label>
          <div class="playground__key">
            <code v-if="apiKey">{{ keyDisplay }}</code>
            <span v-else class="text-muted">{{ $t('studioApi.no_api_key') }}</span>
            <button v-if="apiKey" type="button" class="btn btn-sm btn-outline-secondary" @click="showKey = !showKey">
              {{ showKey ? $t('common.hide') : $t('common.show') }}
            </button>
            <button type="button" class="btn btn-sm btn-primary" :disabled="resettingKey" @click="onResetKey">
              {{ resettingKey ? $t('studioApi.generating') : (apiKey ? $t('studioApi.regenerate') : $t('studioApi.generate_key')) }}
            </button>
          </div>
        </div>

        <div class="playground__section">
          <label class="playground__label">{{ $t('studioApi.design') }}</label>
          <select v-model="designId" class="form-control" :disabled="loadingTemplates">
            <option value="">{{ $t('studioApi.select_design') }}</option>
            <option v-for="t in templates" :key="t.id" :value="t.id">
              {{ t.name }} · {{ t.width }}×{{ t.height }}
            </option>
          </select>
          <p v-if="!loadingTemplates && !templates.length" class="playground__hint">
            {{ $t('studioApi.no_designs_yet') }}
            <router-link to="/ai/studio">{{ $t('studioApi.create_one') }}</router-link>
            {{ $t('studioApi.to_test_api') }}
          </p>
        </div>

        <div class="playground__section">
          <label class="playground__label">{{ $t('studioApi.endpoint') }}</label>
          <div class="playground__endpoints">
            <button
              v-for="ep in endpoints"
              :key="ep.id"
              type="button"
              class="playground__endpoint"
              :class="{ active: endpoint === ep.id }"
              @click="endpoint = ep.id"
            >
              <span class="playground__verb" :class="ep.method.toLowerCase()">{{ ep.method }}</span>
              <span>{{ ep.label }}</span>
            </button>
          </div>
        </div>

        <div v-if="endpoint !== 'list'" class="playground__row">
          <div class="playground__section">
            <label class="playground__label">{{ $t('studioApi.page_label') }}</label>
            <input v-model.number="page" type="number" min="1" class="form-control" />
          </div>
          <div v-if="isDownload" class="playground__section">
            <label class="playground__label">{{ $t('studioApi.format') }}</label>
            <select v-model="format" class="form-control">
              <option value="png">PNG</option>
              <option value="jpeg">JPEG</option>
            </select>
          </div>
        </div>

        <div v-if="isDownload" class="playground__section">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <label class="playground__label mb-0">{{ $t('studioApi.field_overrides') }}</label>
            <button
              type="button"
              class="btn btn-sm btn-link p-0"
              :disabled="!designId || loadingFields"
              @click="loadFields"
            >
              {{ loadingFields ? $t('common.loading') : $t('studioApi.load_page_fields', { page }) }}
            </button>
          </div>
          <p class="playground__hint mb-2">{{ $t('studioApi.render_fields_hint') }}</p>
          <div v-if="fieldRows.length" class="playground__fields">
            <div
              v-for="(row, idx) in fieldRows"
              :key="row.key + '-' + idx"
              class="playground__field"
              :class="{ 'playground__field--image': row.type === 'image' }"
            >
              <code class="playground__field-key" :title="row.label">{{ row.key }}</code>
              <textarea
                v-if="row.type === 'text'"
                v-model="row.value"
                class="form-control form-control-sm playground__field-input"
                rows="2"
                :placeholder="row.placeholder"
              />
              <div v-else class="playground__image-field-wrap">
                <div v-if="fieldImagePreview(row.value)" class="playground__field-preview">
                  <img
                    :key="fieldImagePreview(row.value)"
                    :src="fieldImagePreview(row.value)"
                    alt=""
                    @error="onFieldPreviewError"
                  />
                </div>
                <div class="playground__image-field">
                  <input
                    v-model="row.value"
                    class="form-control form-control-sm"
                    :placeholder="row.placeholder"
                  />
                  <label class="btn btn-sm btn-outline-secondary playground__upload-btn mb-0" :title="row.uploading ? $t('common.uploading') : $t('studioApi.upload_image')">
                    <i :class="row.uploading ? 'fas fa-spinner fa-spin' : 'fas fa-upload'"></i>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      class="d-none"
                      :disabled="row.uploading"
                      @change="onImageUpload($event, idx)"
                    />
                  </label>
                </div>
              </div>
              <button type="button" class="btn btn-sm btn-link text-danger p-0" :title="$t('common.remove')" @click="removeField(idx)">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary mt-2" @click="addField">
            <i class="fas fa-plus mr-1"></i> {{ $t('studioApi.add_field') }}
          </button>
        </div>

        <div class="playground__section">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <label class="playground__label mb-0">{{ $t('studioApi.request_url') }}</label>
            <button
              type="button"
              class="btn btn-sm playground__postman-btn"
              :title="$t('studioApi.open_in_postman_hint')"
              @click="openInPostman"
            >
              <i class="fas fa-external-link-alt playground__postman-icon"></i>
              {{ $t('studioApi.open_in_postman') }}
            </button>
          </div>
          <code class="playground__url">{{ requestUrl }}</code>
        </div>

        <button
          type="button"
          class="btn btn-primary playground__send"
          :disabled="sending || !canSend"
          @click="sendRequest"
        >
          <i :class="sending ? 'fas fa-spinner fa-spin' : 'fas fa-play'" class="mr-1"></i>
          {{ sending ? $t('studioApi.sending') : $t('studioApi.send_request') }}
        </button>
      </div>

      <div ref="playgroundRail" class="playground__rail">
      <div class="playground__col" :style="dockStyle">
        <div class="playground__example userDatatable global-shadow border bg-white radius-xl">
          <button type="button" class="playground__example-toggle" @click="showExample = !showExample">
            <span>
              <i class="fas fa-code mr-1"></i>
              {{ $t('studioApi.request_example') }}
            </span>
            <i class="fas" :class="showExample ? 'fas fa-chevron-up' : 'fas fa-caret-down'"></i>
          </button>
          <div v-show="showExample" class="playground__example-body">
            <div class="playground__example-bar">
              <div class="playground__example-langs">
                <button
                  v-for="tab in exampleLangs"
                  :key="tab.id"
                  type="button"
                  class="playground__example-lang"
                  :class="{ active: exampleLang === tab.id }"
                  @click="exampleLang = tab.id"
                >
                  {{ tab.label }}
                </button>
              </div>
              <button type="button" class="playground__example-copy" :title="$t('common.copy')" @click="copyExample">
                <i class="fas fa-copy"></i>
              </button>
            </div>
            <pre class="playground__body playground__body--example" v-html="highlightedExample"></pre>
          </div>
        </div>

        <div class="playground__response userDatatable global-shadow border bg-white radius-xl">
        <div class="playground__response-head">
          <h5 class="mb-0">{{ $t('studioApi.response') }}</h5>
          <div v-if="responseStatus" class="playground__status">
            <span class="playground__status-code" :class="statusClass">{{ responseStatus }}</span>
            <span class="text-muted">{{ responseType }}</span>
          </div>
        </div>

        <div v-if="!hasResponse" class="playground__empty">
          <i class="fas fa-terminal"></i>
          <p>{{ $t('studioApi.run_request_empty') }}</p>
        </div>

        <div v-else-if="responseImage" class="playground__preview">
          <img :src="responseImage" alt="Rendered design" />
          <div class="playground__preview-actions">
            <a :href="responseImage" download="design.png" class="btn btn-sm btn-outline-primary">{{ $t('studioApi.download') }}</a>
          </div>
        </div>

          <pre v-else class="playground__body">{{ responseBody }}</pre>
        </div>
      </div>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { uploadPublicStudioImage } from '@/api/studio'
import { buildStudioApiExample } from '@/utils/studioApiExamples'
import { buildStudioPostmanCollection, downloadStudioPostmanCollection } from '@/utils/studioPostman'
import { highlightCode } from '@/utils/codeHighlight'
import { publicMediaUrl } from '@/utils/media'

export default {
  name: 'StudioApiPlayground',
  data() {
    return {
      showKey: false,
      resettingKey: false,
      designId: '',
      endpoint: 'downloadPost',
      page: 1,
      format: 'png',
      fieldRows: [],
      loadingFields: false,
      sending: false,
      showExample: false,
      exampleLang: 'js',
      responseStatus: null,
      responseType: '',
      responseBody: '',
      responseImage: '',
      dockStyle: {},
    }
  },
  computed: {
    endpoints() {
      return [
        { id: 'list', method: 'GET', label: this.$t('studioApi.nav_list_designs') },
        { id: 'meta', method: 'GET', label: this.$t('studioApi.nav_get_metadata') },
        { id: 'downloadPost', method: 'POST', label: this.$t('studioApi.nav_render_json') },
      ]
    },
    exampleLangs() {
      return [
        { id: 'curl', label: 'cURL' },
        { id: 'js', label: 'JavaScript' },
        { id: 'python', label: 'Python' },
      ]
    },
    ...mapGetters('ai', ['templates', 'loading']),
    ...mapGetters('auth', ['user']),
    loadingTemplates() {
      return this.loading && !this.templates.length
    },
    apiKey() {
      return (this.user && this.user.api_key) || ''
    },
    keyDisplay() {
      if (!this.apiKey) return ''
      return this.showKey ? this.apiKey : this.maskKey(this.apiKey)
    },
    apiBase() {
      return String(process.env.VUE_APP_BASE_URL || '').replace(/\/$/, '')
    },
    isDownload() {
      return this.endpoint === 'downloadPost'
    },
    canSend() {
      if (!this.apiKey) return false
      if (this.endpoint === 'list') return true
      return !!this.designId
    },
    hasResponse() {
      return this.responseStatus !== null
    },
    statusClass() {
      if (!this.responseStatus) return ''
      if (this.responseStatus >= 200 && this.responseStatus < 300) return 'ok'
      if (this.responseStatus >= 400) return 'err'
      return ''
    },
    requestUrl() {
      return this.studioRequestPath(this.apiBase)
    },
    requestExample() {
      return buildStudioApiExample({
        endpoint: this.endpoint,
        lang: this.exampleLang,
        url: this.requestUrl,
        apiKey: this.apiKey,
        page: this.page,
        format: this.format,
        fields: this.fieldMap(),
      })
    },
    highlightedExample() {
      return highlightCode(this.requestExample, this.exampleLang)
    },
  },
  watch: {
    designId() {
      this.fieldRows = []
      this.clearResponse()
      if (this.isDownload && this.designId) this.loadFields()
    },
    endpoint(val) {
      this.clearResponse()
      if (val === 'downloadPost' && this.designId) this.loadFields()
    },
    page() {
      this.fieldRows = []
      this.clearResponse()
    },
  },
  async created() {
    try {
      await this.fetchProfile()
    } catch (e) {
      /* keep persisted user */
    }
    await this.fetchTemplates()
    if (this.templates.length) this.designId = this.templates[0].id
    if (this.isDownload && this.designId) await this.loadFields()
    this.$nextTick(() => this.syncDock())
  },
  mounted() {
    this.$nextTick(() => this.syncDock())
    window.addEventListener('resize', this.syncDock, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncDock)
    this.revokeImage()
  },
  methods: {
    syncDock() {
      if (typeof window === 'undefined' || window.innerWidth < 992) {
        this.dockStyle = {}
        return
      }
      const rail = this.$refs.playgroundRail
      if (!rail) return
      const rect = rail.getBoundingClientRect()
      this.dockStyle = {
        position: 'fixed',
        top: 'calc(64px + 1rem)',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: 'calc(100vh - 64px - 2rem)',
        overflowY: 'auto',
        zIndex: 12,
      }
    },
    ...mapActions('ai', ['fetchTemplates', 'fetchPublicStudio']),
    ...mapActions('user', ['fetchProfile', 'resetApiKey']),
    studioRequestPath(base) {
      const root = String(base || '').replace(/\/$/, '')
      if (this.endpoint === 'list') return `${root}/public/studio`
      if (!this.designId) return `${root}/public/studio/{id}`
      const design = `${root}/public/studio/${this.designId}`
      if (this.endpoint === 'meta') return `${design}?page=${this.page}`
      return `${design}/download`
    },
    maskKey(key) {
      if (!key) return ''
      if (key.length <= 8) return '••••••••'
      return `${key.slice(0, 8)}${'•'.repeat(Math.min(24, key.length - 8))}`
    },
    fieldImagePreview(value) {
      const trimmed = String(value || '').trim()
      if (!trimmed) return ''
      return publicMediaUrl(trimmed)
    },
    onFieldPreviewError(event) {
      const wrap = event && event.target && event.target.closest('.playground__field-preview')
      if (wrap) wrap.style.display = 'none'
    },
    async copyExample() {
      try {
        await navigator.clipboard.writeText(this.requestExample)
        this.$toast.success(this.$t('studioApi.copied'))
      } catch (e) {
        this.$toast.error(this.$t('studioApi.copy_failed'))
      }
    },
    openInPostman() {
      const collection = buildStudioPostmanCollection({
        apiKey: this.apiKey,
        designId: this.designId,
        page: this.page,
        format: this.format,
      })
      try {
        downloadStudioPostmanCollection(collection)
        this.$toast.success(this.$t('studioApi.open_in_postman_done'))
      } catch (e) {
        this.$toast.error(this.$t('studioApi.open_in_postman_failed'))
      }
    },
    addField() {
      this.fieldRows.push({ key: '', value: '', defaultValue: '', label: '', type: 'text', placeholder: 'value' })
    },
    removeField(idx) {
      this.fieldRows.splice(idx, 1)
    },
    async onImageUpload(event, idx) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (!this.apiKey) {
        this.$toast.error(this.$t('studioApi.generate_key_first'))
        return
      }
      const row = this.fieldRows[idx]
      if (!row) return
      row.uploading = true
      try {
        const { data } = await uploadPublicStudioImage(file, this.apiKey)
        row.value = (data.data && data.data.url) || ''
        this.$toast.success(this.$t('studioApi.image_uploaded'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('studioApi.upload_failed'))
      } finally {
        row.uploading = false
      }
    },
    fieldMap() {
      const out = {}
      this.fieldRows.forEach((row) => {
        if (!row.key) return
        const val = String(row.value ?? '').trim()
        const def = String(row.defaultValue ?? '').trim()
        if (!val || val === def) return
        out[row.key] = row.value
      })
      return out
    },
    async loadFields() {
      if (!this.designId || !this.apiKey) return
      this.loadingFields = true
      try {
        const design = await this.fetchPublicStudio({ id: this.designId, page: this.page })
        this.fieldRows = (design.fields || []).map((f) => ({
          key: f.key,
          value: f.value || '',
          defaultValue: f.value || '',
          label: f.label || f.key,
          type: f.type || 'text',
          uploading: false,
          placeholder: f.type === 'image' ? this.$t('studioApi.field_image_placeholder') : f.value || '',
        }))
        if (!this.fieldRows.length) {
          this.$toast.info(this.$t('studioApi.no_editable_fields', { page: this.page }))
        }
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('studioApi.failed_load_fields'))
      } finally {
        this.loadingFields = false
      }
    },
    clearResponse() {
      this.revokeImage()
      this.responseStatus = null
      this.responseType = ''
      this.responseBody = ''
    },
    revokeImage() {
      if (this.responseImage) URL.revokeObjectURL(this.responseImage)
      this.responseImage = ''
    },
    async sendRequest() {
      if (!this.canSend) {
        if (!this.apiKey) this.$toast.error(this.$t('studioApi.generate_key_first'))
        else this.$toast.error(this.$t('studioApi.select_design_error'))
        return
      }
      this.sending = true
      this.clearResponse()
      try {
        const headers = { 'X-API-Key': this.apiKey }
        let url
        let options = { method: 'GET', headers }
        if (this.endpoint === 'downloadPost') {
          url = `${this.apiBase}/public/studio/${this.designId}/download`
          options = {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              page: this.page,
              format: this.format,
              fields: this.fieldMap(),
            }),
          }
        } else if (this.endpoint === 'list') {
          url = `${this.apiBase}/public/studio`
        } else {
          url = `${this.apiBase}/public/studio/${this.designId}?page=${this.page}`
        }
        const res = await fetch(url, options)
        this.responseStatus = res.status
        this.responseType = res.headers.get('content-type') || 'unknown'
        if (this.responseType.indexOf('application/json') !== -1) {
          const json = await res.json()
          this.responseBody = JSON.stringify(json, null, 2)
        } else {
          const blob = await res.blob()
          this.responseImage = URL.createObjectURL(blob)
        }
      } catch (e) {
        this.responseStatus = 0
        this.responseType = 'error'
        this.responseBody = String(e.message || e)
        this.$toast.error(this.$t('studioApi.request_failed'))
      } finally {
        this.sending = false
      }
    },
    async onResetKey() {
      const hadKey = !!this.apiKey
      if (hadKey) {
        const ok = await this.$confirm(this.$t('studioApi.regenerate_confirm_full'), { danger: true })
        if (!ok) return
      }
      this.resettingKey = true
      try {
        await this.resetApiKey()
        this.$toast.success(hadKey ? this.$t('studioApi.key_regenerated') : this.$t('studioApi.key_generated'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('studioApi.failed_generate_key'))
      } finally {
        this.resettingKey = false
      }
    },
  },
}
</script>

<style scoped>
.playground {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 420px);
  gap: 1.25rem;
  align-items: start;
}
.playground__rail {
  min-width: 0;
  align-self: start;
}
.playground__col {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
  -webkit-overflow-scrolling: touch;
}
.playground__request,
.playground__response {
  padding: 1.25rem 1.5rem;
}
.playground__example-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.85rem 1.25rem;
  border: 0;
  background: transparent;
  color: var(--wc-text-heading);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.playground__example-toggle:hover {
  color: var(--wc-primary);
}
.playground__example-toggle i.fas-code {
  color: var(--wc-primary);
}
.playground__example-body {
  padding: 0 1.25rem 1rem;
}
.playground__example-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.playground__example-langs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}
.playground__example-lang {
  border: 1px solid var(--wc-border);
  background: var(--wc-bg-subtle);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: var(--wc-text-muted);
  cursor: pointer;
  line-height: 1.5;
}
.playground__example-lang:hover {
  border-color: var(--wc-primary);
  color: var(--wc-primary);
}
.playground__example-lang.active {
  border-color: var(--wc-primary);
  background: var(--wc-primary-light);
  color: var(--wc-primary);
}
.playground__example-copy {
  width: 28px;
  height: 28px;
  border: 1px solid var(--wc-border);
  border-radius: 6px;
  background: #fff;
  color: var(--wc-text-muted);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
}
.playground__example-copy:hover {
  border-color: var(--wc-primary);
  color: var(--wc-primary);
}
.playground__body--example {
  max-height: 148px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.55;
  padding: 10px 12px;
  tab-size: 2;
  overflow: auto;
  white-space: pre;
  word-break: normal;
}
.playground__body--example ::v-deep .tok-key { color: #7dd3fc; }
.playground__body--example ::v-deep .tok-str { color: #86efac; }
.playground__body--example ::v-deep .tok-num { color: #fbbf24; }
.playground__body--example ::v-deep .tok-bool { color: #c4b5fd; }
.playground__body--example ::v-deep .tok-kw { color: #93c5fd; }
.playground__body--example ::v-deep .tok-flag { color: #fda4af; }
.playground__body--example ::v-deep .tok-comment { color: #71717a; }
.playground__section {
  margin-bottom: 1.1rem;
}
.playground__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--wc-text-muted);
  margin-bottom: 6px;
}
.playground__key {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.playground__key code {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  word-break: break-all;
  background: var(--wc-bg-subtle);
  padding: 6px 10px;
  border-radius: 6px;
}
.playground__hint {
  font-size: 13px;
  color: var(--wc-text-muted);
  margin: 6px 0 0;
}
.playground__endpoints {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.playground__endpoint {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1px solid var(--wc-border);
  background: var(--wc-bg-card);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: var(--wc-text);
  cursor: pointer;
}
.playground__endpoint:hover {
  background: var(--wc-bg-subtle);
}
.playground__endpoint.active {
  border-color: var(--wc-primary);
  background: var(--wc-primary-light);
  color: var(--wc-primary);
}
.playground__verb {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
  min-width: 36px;
}
.playground__verb.get { color: var(--wc-success); }
.playground__verb.post { color: var(--wc-info); }
.playground__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.playground__fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.playground__field {
  display: grid;
  grid-template-columns: 120px 1fr 24px;
  gap: 8px;
  align-items: start;
}
.playground__field--image {
  align-items: center;
}
.playground__field-input {
  min-height: 36px;
  resize: vertical;
  line-height: 1.4;
}
.playground__image-field-wrap {
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
}
.playground__field-preview {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 6px;
  border: 1px solid var(--wc-border);
  background: var(--wc-bg-subtle);
  overflow: hidden;
}
.playground__field-preview img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.playground__image-field {
  display: flex;
  gap: 6px;
  align-items: center;
  flex: 1;
  min-width: 0;
}
.playground__image-field .form-control {
  flex: 1;
  min-width: 0;
}
.playground__upload-btn {
  flex-shrink: 0;
  cursor: pointer;
}
.playground__field-key {
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: var(--wc-bg-subtle);
  padding: 4px 6px;
  border-radius: 4px;
}
.playground__url {
  display: block;
  font-size: 12px;
  word-break: break-all;
  background: var(--wc-bg-subtle);
  padding: 10px 12px;
  border-radius: 8px;
  color: var(--wc-text);
}
.playground__postman-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-color: #ff6c37;
  color: #ff6c37;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
}
.playground__postman-btn:hover:not(:disabled) {
  background: #ff6c37;
  border-color: #ff6c37;
  color: #fff;
}
.playground__postman-btn:disabled {
  opacity: 0.45;
}
.playground__postman-icon {
  font-size: 11px;
}
.playground__send {
  width: 100%;
}
.playground__response-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.playground__status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.playground__status-code {
  font-weight: 700;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--wc-bg-subtle);
}
.playground__status-code.ok {
  color: var(--wc-success);
  background: rgba(40, 167, 69, 0.1);
}
.playground__status-code.err {
  color: var(--wc-danger);
  background: rgba(220, 53, 69, 0.1);
}
.playground__empty {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--wc-text-muted);
}
.playground__empty i {
  font-size: 2rem;
  opacity: 0.35;
  margin-bottom: 12px;
}
.playground__body {
  margin: 0;
  padding: 14px;
  background: #1e2433;
  color: #e8eaed;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
  max-height: 520px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
.playground__preview {
  text-align: center;
}
.playground__preview img {
  max-width: 100%;
  border-radius: 8px;
  border: 1px solid var(--wc-border);
}
.playground__preview-actions {
  margin-top: 12px;
}
@media (max-width: 991px) {
  .playground {
    grid-template-columns: 1fr;
  }
}
</style>
